import AbstractLoaderStore from "./pages/AbstractLoaderStore";
import { IObjectMap, Row } from "../interfaces/common";
import { action, observable } from "mobx";
import { Pagination } from "../models/Pagination";
import moment from "moment";
import {
  CreateFleetExpensesTransportParameters,
  FleetExpensesTransport,
  GetFleetExpensesTransportData,
} from "../models/FleetExpenses/FleetExpensesTransport";
import { OptionMap } from "../helpers/optionMap";
import { Nullable, WithId } from "../models/Common";
import { unwrapPromise } from "../helpers/unwrapPromise";
import {
  FleetExpensesSpendingType,
  GetFleetExpensesSpendingTypeData,
} from "../models/FleetExpenses/FleetExpensesSpendingType";
import {
  FleetCardAssignment,
  FleetDriver,
  FleetExpenseId,
  FleetExpenseParameters,
  FleetExpenseRowType,
  FleetExpensesModel,
  FleetExpensesModelAny,
  PaymentType,
} from "../models/FleetExpenses/FleetExpensesModel";
import { addMessage, callMessages } from "../helpers/flashMessage";
import { isFleetExpenseOfType } from "../components/tables/fleetExpenses/config";
import { CurrencyType } from "../constants/currency";

const logisticDateRange = {
  start_date: moment().subtract(20, "days"),
  end_date: moment().subtract(-10, "days"),
};

const defaultPagination = {
  total: 0,
  per_page: 0,
  current_page: 0,
  last_page: 999,
  from: 0,
  to: 0,
};

const defaultParameters = {
  ...logisticDateRange,
  page: 0,
};

export type FleetExpensesGridFiltersObject = {
  tc_id: Nullable<string>;
  spending_type_id: Nullable<string>;
  driver_id: Nullable<string>;
  currency: Nullable<CurrencyType>;
  payment_type: Nullable<PaymentType>;
};

export type FleetExpensesGridFilter = keyof FleetExpensesGridFiltersObject;

class FleetExpensesStore extends AbstractLoaderStore {
  constructor() {
    super();
    this._parameters = defaultParameters;
  }

  protected _pagination: Pagination = defaultPagination;

  @observable
  selectedRows: Row[] = [];

  @observable
  expandedRows: Row[] = [];

  @observable
  editingKeys: number[] = [];

  private tc: Nullable<OptionMap<string>> = null;
  private tcFetch: Promise<Nullable<OptionMap<string>>> | null = null;

  private spendingTypes: Nullable<OptionMap<string>> = null;
  private spendingTypesFetch: Promise<
    Nullable<OptionMap<string>>
  > | null = null;

  private drivers: Nullable<OptionMap<string>> = null;
  private driversFetch: Promise<Nullable<OptionMap<string>>> | null = null;

  private gridFilters: FleetExpensesGridFiltersObject = {
    tc_id: null,
    spending_type_id: null,
    driver_id: null,
    currency: null,
    payment_type: null,
  };

  init() {
    void this.getTransports();
    void this.getSpendingTypes();
  }

  getFilter<Filter extends FleetExpensesGridFilter>(
    filter: Filter
  ): FleetExpensesGridFiltersObject[Filter] {
    return this.gridFilters[filter];
  }

  setFilter<Filter extends FleetExpensesGridFilter>(
    filter: Filter,
    value: FleetExpensesGridFiltersObject[Filter]
  ): void {
    this.gridFilters[filter] = value;
  }

  @action
  load(parameters: IObjectMap | null, needUpdateData = false): void {
    console.log(`Parameters: ${JSON.stringify(parameters)}`);
    if (parameters) {
      console.log(`Parameters: ${JSON.stringify(this._parameters)}`);
      this._parameters = {
        ...this._parameters,
        ...parameters,
        page: needUpdateData ? 1 : this._pagination.current_page + 1,
      };
    }

    this.getLoadResponse(this._parameters)
      .then(
        async (response): Promise<void> => {
          const items: FleetExpensesModelAny[] = (await response.json()).data;
          let driverCount: number = 1;
          let tcCount: number = 1;
          let stCount: number = 1;
          const applyIds = (items: FleetExpensesModelAny[]): void => {
            items.forEach((item: FleetExpensesModelAny) => {
              if (isFleetExpenseOfType(item, "driver")) {
                (item as WithId<FleetExpensesModelAny>).id = `driver-${driverCount}`;
                applyIds(item.children);
                driverCount++;
              } else if (isFleetExpenseOfType(item, "tc")) {
                (item as WithId<FleetExpensesModelAny>).id = `tc-${tcCount}`;
                applyIds(item.children);
                tcCount++;
              } else if (isFleetExpenseOfType(item, "spending-type")) {
                (item as WithId<FleetExpensesModelAny>).id = `st-${stCount}`;
                stCount++;
              }
            });
          };
          applyIds(items);
          this._data = items;
        }
      )
      .catch(() => {
        this._data = [];
        this._pagination = {
          total: 0,
          per_page: 0,
          current_page: 0,
          last_page: 0,
          from: 0,
          to: 0,
        };
      });
  }

  @action
  changeSelectedRows(ids: Row[]): void {
    this.selectedRows = ids;
  }

  async wrapWithLoading<T extends unknown>(
    callback: () => Promise<T>
  ): Promise<T> {
    this.isLoadingTableData = true;
    const result: T = await callback();
    this.isLoadingTableData = false;
    return result;
  }

  async fetchTransports(parameters: IObjectMap): Promise<Response> {
    return this.wrapWithLoading(async () => {
      return await this.apiManager.loadFleetExpensesTransports(parameters);
    });
  }

  private async setupTransports(): Promise<void> {
    try {
      const result: Nullable<GetFleetExpensesTransportData> = await unwrapPromise<GetFleetExpensesTransportData>(
        this.fetchTransports({ pageSize: 10000 })
      );
      if (result) {
        this.tc = new OptionMap<string>(
          result.data.reduce((acc, curr) => {
            acc[curr.id.toString()] = curr.tc_number;
            return acc;
          }, {} as Record<string, string>)
        );
      }
    } catch {
      console.warn("Error when fetching transports");
      this.tc = null;
    }
  }

  async getTransports(): Promise<Nullable<OptionMap<string>>> {
    if (this.tc) {
      return this.tc;
    }

    if (this.tcFetch) {
      return this.tcFetch;
    }

    console.log("Fetching transports");

    this.tcFetch = this.setupTransports()
      .then(() => {
        return this.tc ?? null;
      })
      .finally(() => {
        this.tcFetch = null;
      });

    return this.tcFetch;
  }

  private async setupDrivers(): Promise<void> {
    const result: Nullable<FleetDriver[]> = await unwrapPromise<FleetDriver[]>(
      this.apiManager.loadFleetDrivers()
    );
    if (result) {
      this.drivers = new OptionMap<string>(
        result.reduce((acc, driver) => {
          acc[driver.id.toString()] = driver.name;
          return acc;
        }, {} as Record<string, string>)
      );
    } else {
      this.drivers = null;
    }
  }

  async getDrivers(): Promise<Nullable<OptionMap<string>>> {
    if (this.drivers) {
      return this.drivers;
    }

    if (this.driversFetch) {
      return this.driversFetch;
    }

    this.driversFetch = this.setupDrivers()
      .then(() => this.drivers)
      .finally(() => {
        this.driversFetch = null;
      });

    return this.driversFetch;
  }

  async getDriverCardAssignments(
    driverId: FleetDriver["id"],
    date: string
  ): Promise<Nullable<FleetCardAssignment[]>> {
    return unwrapPromise<FleetCardAssignment[]>(
      this.apiManager.loadFleetDriverCardAssignments(driverId, date)
    );
  }

  async createTransport(
    parameters: CreateFleetExpensesTransportParameters
  ): Promise<Response> {
    const promise: Promise<Response> = this.wrapWithLoading(async () => {
      return await this.apiManager.createFleetExpensesTransport(parameters);
    });
    unwrapPromise<{ entity: FleetExpensesTransport }>(promise).then(
      (result: Nullable<{ entity: FleetExpensesTransport }>) => {
        if (result) {
          addMessage(
            "Transport created successfully, you can now use it to create expenses",
            "success"
          );
          if (this.tc) {
            this.tc.addOption(
              result.entity.id.toString(),
              result.entity.tc_number
            );
          }
        } else {
          addMessage("Error when creating transport", "error");
        }
        callMessages();
      }
    );

    return promise;
  }

  async createExpense(parameters: FleetExpenseParameters): Promise<Response> {
    return this.wrapWithLoading(async () => {
      return await this.apiManager.createFleetExpense(parameters);
    });
  }

  async updateExpense(
    id: FleetExpenseId,
    parameters: FleetExpenseParameters
  ): Promise<Response> {
    return this.wrapWithLoading(async () => {
      return await this.apiManager.updateFleetExpense(id, parameters);
    });
  }

  async deleteExpense(id: FleetExpenseId): Promise<Response> {
    return this.wrapWithLoading(async () => {
      return await this.apiManager.deleteFleetExpense(id);
    });
  }

  async fetchSpendingTypes(parameters: IObjectMap): Promise<Response> {
    return this.wrapWithLoading(async () => {
      return await this.apiManager.loadFleetExpensesSpendingTypes(parameters);
    });
  }

  private async setupSpendingTypes(): Promise<void> {
    try {
      const result: Nullable<GetFleetExpensesSpendingTypeData> = await unwrapPromise<GetFleetExpensesSpendingTypeData>(
        this.fetchSpendingTypes({ pageSize: 10000 })
      );
      if (result) {
        this.spendingTypes = new OptionMap<string>(
          result.reduce(
            (acc: Record<string, string>, curr: FleetExpensesSpendingType) => {
              acc[curr.id.toString()] = curr.name;
              return acc;
            },
            {} as Record<string, string>
          )
        );
      }
    } catch {
      console.warn("Error when fetching spending types");
      this.spendingTypes = null;
    }
  }

  async getSpendingTypes(): Promise<Nullable<OptionMap<string>>> {
    if (this.spendingTypes) {
      return this.spendingTypes;
    }

    if (this.spendingTypesFetch) {
      return this.spendingTypesFetch;
    }

    this.spendingTypesFetch = this.setupSpendingTypes()
      .then(() => {
        return this.spendingTypes ?? null;
      })
      .finally(() => {
        this.spendingTypesFetch = null;
      });

    return this.spendingTypesFetch;
  }

  async getLoadResponse(parameters: IObjectMap): Promise<Response> {
    const paramsCopy = { ...parameters };
    if ("start_date" in paramsCopy && paramsCopy.start_date) {
      paramsCopy.start_date = (paramsCopy.start_date as moment.Moment).format(
        "YYYY-MM-DD"
      );
    }
    if ("end_date" in paramsCopy && paramsCopy.end_date) {
      paramsCopy.end_date = (paramsCopy.end_date as moment.Moment).format(
        "YYYY-MM-DD"
      );
    }
    return this.wrapWithLoading(async () => {
      return await this.apiManager.loadFleetExpensesByClients(paramsCopy);
    });
  }
}

export default new FleetExpensesStore();
